import { Injectable } from '@angular/core';
import { BehaviorSubject, finalize, map, Observable, of } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { IProperty } from '@app/@shared/model/property.model';
import { SERVER_API_URL } from '@app/app.constants';
import { createRequestOption } from '../web/request-util';
import {
  PayloadFormOne,
  PayloadFormTwo,
  PublicationsData,
  PublicationsDataFormat,
  PublicationsDataResponse,
  TypeProperty,
} from '@app/@shared/model/sell.model';

@Injectable({
  providedIn: 'root',
})
export class SellService {
  private usagesStatus: any[] = [];
  private provinces: any[] = [];
  private provincesWithGreat: any[] = [];
  private propertyType: any[] = [];
  private leaseTypes: any[] = [];

  public loadingSend = new BehaviorSubject<boolean>(false);

  get loadingSend$(): Observable<boolean> {
    return this.loadingSend.asObservable();
  }

  constructor(private http: HttpClient) {}

  getImages(data: any, queries: any): Observable<any> {
    const options = createRequestOption(queries);
    return this.http.post(SERVER_API_URL + 'properties/images', data, {
      params: options,
    });
  }

  getProperties(queries: any): Observable<any> {
    const options = createRequestOption(queries);
    return this.http.get(SERVER_API_URL + 'properties', {
      params: options,
      observe: 'response',
    });
  }

  getSimilarProperties(idProperty: number): Observable<any> {
    return this.http.get(
      SERVER_API_URL + `properties/similar-properties/${idProperty}`
    );
  }

  getLeaseTypes(): Observable<any> {
    if (this.leaseTypes.length > 0) {
      return of(this.leaseTypes);
    }
    return this.http.get(SERVER_API_URL + 'properties/lease-types');
  }

  getPropertyType(): Observable<TypeProperty[]> {
    if (this.propertyType.length > 0) {
      return of(this.propertyType);
    }
    return this.http.get<TypeProperty[]>(SERVER_API_URL + 'properties/types');
  }
  setLeaseTypes(leaseTypes: any[]): void {
    this.leaseTypes = leaseTypes;
  }
  setPropertyType(propertyTypes: any[]): void {
    this.propertyType = propertyTypes;
  }
  getOrientations(): Observable<any> {
    return this.http.get(SERVER_API_URL + 'properties/orientations');
  }
  getUsageStatuss(): Observable<any> {
    if (this.usagesStatus.length > 0) {
      return of(this.usagesStatus);
    }
    return this.http.get(SERVER_API_URL + 'properties/usage-status');
  }
  setUsageStatuss(usageStatuss: any[]): void {
    this.usagesStatus = usageStatuss;
  }
  getMaxPrice(): Observable<any> {
    return this.http.get(SERVER_API_URL + 'publications/publication-price/max');
  }
  getProvinces(): Observable<any> {
    return this.http.get(SERVER_API_URL + 'locations/provinces');
  }
  getProvincesWithProperties(): Observable<any> {
    return this.http.get(
      SERVER_API_URL + 'locations/provinces-with-properties'
    );
  }
  getProvincesWithPublications(noHardRefresh: boolean = true): Observable<any> {
    if (noHardRefresh && this.provinces.length > 0) {
      return of(this.provinces);
    }
    return this.http.get(
      SERVER_API_URL + 'locations/provinces-with-publications'
    );
  }
  setProvinces(provinces: any[]): void {
    this.provinces = provinces;
  }

  getProvincesWithGreatPublications(
    noHardRefresh: boolean = true
  ): Observable<any> {
    if (noHardRefresh && this.provincesWithGreat.length > 0) {
      return of(this.provincesWithGreat);
    }
    return this.http.get(
      SERVER_API_URL + 'locations/provinces-with-great-publications'
    );
  }

  setProvincesWithGreat(provinces: any[]): void {
    this.provincesWithGreat = provinces;
  }

  getLocationsByProvince(idProvince: number): Observable<any> {
    return this.http.get(
      SERVER_API_URL + `locations/municipalities/${idProvince}`
    );
  }
  getLocations(): Observable<any> {
    return this.http.get(SERVER_API_URL + `locations/municipalities`);
  }
  registerProperty(property: IProperty): Observable<any> {
    return this.http.post(SERVER_API_URL + 'properties', property, {
      observe: 'response',
    });
  }
  registerPropertyOffer(offer: any): Observable<any> {
    return this.http.post(SERVER_API_URL + 'properties/offer', offer, {
      observe: 'response',
    });
  }
  registerDocuments(documents: any): Observable<any> {
    return this.http.post(SERVER_API_URL + 'properties/documents', documents, {
      observe: 'response',
    });
  }
  changePublicationImportancy(
    id_property: number,
    is_important_property: boolean
  ): Observable<any> {
    return this.http.post(
      `${SERVER_API_URL}properties/property-change-importancy`,
      {
        id_property,
        is_important_property,
      }
    );
  }
  getPublications(req: any): Observable<PublicationsDataResponse> {
    const options = createRequestOption(req);
    return this.http
      .get<any>(SERVER_API_URL + 'publications', {
        params: options,
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const body = response.body as any;
          const isArrayBody = Array.isArray(body);
          return {
            data: isArrayBody
              ? body.find((r: PublicationsData) => r) ?? []
              : [],
            households: isArrayBody ? body[1] || 0 : 0,
          };
        }),
        map((data) => this.mapData(data))
      );
  }

  mapData(response: PublicationsDataResponse): PublicationsDataResponse {
    const formattedData = response.data.map((property) => {
      const {
        publicationSummary,
        publicationInspection,
        propertyPrice,
        publicationRental,
        propertyHistories,
      } = property;
      const histories = propertyHistories.find((history) => history);
      const rental = publicationRental.find((history) => history);

      if (histories?.property?.documents) {
        histories.property.documents.sort(
          (a: any, b: any) => a.position - b.position
        );
      }

      const dataFormat = {
        is_important_property:
          publicationSummary?.is_important_property ?? false,
        inspection_date: publicationInspection?.inspection_date ?? '',
        reference: publicationSummary?.reference ?? '',
        propertyTypeDes: histories?.property?.propertyType?.description ?? '',
        locationDes: histories?.property?.address?.location?.description ?? '',
        provinceDes:
          histories?.property?.address?.location?.province?.description ?? '',
        isFavorite: histories?.property?.isFavorite ?? false,
        isCheck: property?.isCheck ?? false,
        street: histories?.property?.address?.street ?? '',
        no_street: histories?.property?.address?.no_street ?? '',
        mount: propertyPrice?.mount ?? 0,
        total_covered: publicationSummary?.total_covered ?? 0,
        qty_room: publicationSummary?.qty_room ?? 0,
        qty_toilet: publicationSummary?.qty_toilet ?? 0,
        usageStatusDes_en: publicationSummary.usageStatus?.description_en ?? '',
        usageStatusDes: publicationSummary.usageStatus?.description ?? '',
        price_rental: publicationSummary?.price_rental ?? 0,
        old_rent: publicationSummary?.old_rent ?? false,
        ending_rent_contract: rental?.ending_rent_contract ?? '',
        construction_year: publicationSummary?.construction_year ?? 0,
        area_quality: publicationSummary?.area_quality ?? 0,
        gross_profitability: publicationSummary?.gross_profitability ?? 0,
        images:
          histories?.property?.documents
            ?.map((document: any) => document.thumbs_url)
            ?.slice(0, 1) || [],
        images2: publicationSummary?.main_image
          ? [publicationSummary.main_image]
          : [],
        estado: histories?.propertyState?.description ?? '',
        documents: histories?.property?.documents || [],
        main_image: publicationSummary?.main_image || '',
        is_visitable: publicationSummary?.is_visitable,
      } as PublicationsDataFormat;
      return {
        ...property,
        dataFormat,
      };
    });
    return { ...response, data: formattedData };
  }

  getMarkers(req: any): Observable<any> {
    const options = createRequestOption(req);
    return this.http.get(SERVER_API_URL + 'publications/coordinates', {
      params: options,
      observe: 'response',
    });
  }
  getPublicationById(id: number): Observable<any> {
    return this.http.get(SERVER_API_URL + `publications/${id}`);
  }
  getPublicationByReference(reference: string): Observable<any> {
    return this.http.get(
      SERVER_API_URL + `publications/reference/${reference}`
    );
  }
  registerPublication(propertyId: number): Observable<any> {
    return this.http.post(`${SERVER_API_URL}publications`, { propertyId });
  }
  changePublicationCondition(
    propertyId: number,
    condition: string
  ): Observable<any> {
    return this.http.post(`${SERVER_API_URL}publications/update-condition`, {
      propertyId,
      condition,
    });
  }
  getSimilarPublications(idPublication: number): Observable<any> {
    return this.http.get(
      SERVER_API_URL + `publications/similars/${idPublication}`
    );
  }
  getSuggestedPublication(): Observable<any> {
    return this.http.get(SERVER_API_URL + 'publications/properties/suggested');
  }
  getPublicationReferenceMap(references: string): Observable<any> {
    return this.http.get(
      SERVER_API_URL +
        'public/publication/get-publication-to-show-in-map/' +
        references
    );
  }

  getGeoJSONProvince(id_province: string): Observable<any> {
    return this.http.get(
      SERVER_API_URL + 'public/provinces/' + id_province + '/geo-json'
    );
  }

  postRegisterFormOne(form: PayloadFormOne): Observable<any> {
    this.loadingSend.next(true);
    return this.http
      .post(`${SERVER_API_URL}great-properties/catchment/form-one`, form, {
        observe: 'response',
      })
      .pipe(finalize(() => this.loadingSend.next(false)));
  }

  postRegisterFormTwo(form: PayloadFormTwo): Observable<any> {
    this.loadingSend.next(true);
    return this.http
      .post(`${SERVER_API_URL}great-properties/catchment/form-two`, form, {
        observe: 'response',
      })
      .pipe(finalize(() => this.loadingSend.next(false)));
  }
}
